import { Link } from "react-router-dom";
import { fileUrl } from "@/lib/api";
import { MapPin, Zap, Truck, Package, BadgeCheck, ShieldCheck } from "lucide-react";

const CAT_LABEL = { wheels: "Wheels", tires: "Tires", set: "Wheel + Tire Set" };

export const ListingCard = ({ listing, index = 0 }) => {
  const photo = listing.photos?.[0] ? fileUrl(listing.photos[0]) : null;
  const specs = [];
  if (listing.bolt_pattern) specs.push(listing.bolt_pattern);
  if (listing.diameter && listing.width) specs.push(`${listing.diameter}x${listing.width}`);
  if (listing.offset) specs.push(`ET${listing.offset}`);
  if (listing.tire_size) specs.push(listing.tire_size);

  return (
    <Link
      to={`/listing/${listing.id}`}
      data-testid={`listing-card-${listing.id}`}
      className="group relative bg-[#141414] border border-border rounded-sm overflow-hidden hover:border-blaze/60 transition-colors duration-200 flex flex-col animate-fade-up"
      style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
    >
      {listing.boosted && (
        <div className="absolute top-2 left-2 z-10 bg-blaze text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm flex items-center gap-1">
          <Zap className="w-3 h-3" /> Boosted
        </div>
      )}
      {listing.featured && !listing.boosted && (
        <div className="absolute top-2 left-2 z-10 bg-black/80 text-blaze text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm flex items-center gap-1 border border-blaze/40">
          <BadgeCheck className="w-3 h-3" /> Dealer
        </div>
      )}
      {listing.fitment_verified && (
        <div data-testid={`verified-fit-badge-${listing.id}`} className="absolute top-2 right-2 z-10 bg-emerald-600/90 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" /> Verified Fit
        </div>
      )}
      <div className="h-44 bg-[#1C1C1C] overflow-hidden relative">
        {photo ? (
          <img src={photo} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <Package className="w-10 h-10" />
          </div>
        )}
        <span className="absolute bottom-2 right-2 bg-black/70 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm">
          {CAT_LABEL[listing.category]}
        </span>
      </div>

      <div className="p-4 flex flex-col gap-2 flex-1">
        <h3 className="font-heading font-800 text-base leading-tight line-clamp-2">{listing.title}</h3>
        <div className="text-xs text-muted-foreground uppercase tracking-wide">{listing.brand}{listing.model ? ` · ${listing.model}` : ""}</div>

        {specs.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {specs.map((s, i) => (
              <span key={i} className="spec-block text-[11px] bg-[#0A0A0A] border border-border px-1.5 py-0.5 text-blaze">{s}</span>
            ))}
          </div>
        )}

        <div className="mt-auto pt-3 flex items-end justify-between border-t border-border">
          <div>
            <div className="font-heading font-900 text-xl text-white">${listing.price?.toLocaleString()}</div>
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-0.5">
              <MapPin className="w-3 h-3" /> {listing.location}
              {listing.distance != null && <span data-testid={`card-distance-${listing.id}`} className="text-blaze font-bold ml-1">· {listing.distance < 1 ? "< 1" : listing.distance} mi</span>}
            </div>
          </div>
          <div className="text-muted-foreground" title={listing.fulfillment}>
            {listing.fulfillment === "pickup" ? <MapPin className="w-4 h-4" /> : <Truck className="w-4 h-4" />}
          </div>
        </div>
      </div>
    </Link>
  );
};
